import { AccessService } from 'src/app/core/services/access.service';
import { getTextWithPic } from 'src/app/shared/helpers/textWithPic';
import { GenericResponseList } from 'src/app/shared/interfaces/table.interface';
import { NameWithPic } from 'src/app/shared/interfaces/textWithPic.interface';
import {
  REQUEST_CATEGORY,
  REQUEST_FREQUENCY_TYPE,
} from './enums';
import {
  getDateBagde,
  getRoundBadge,
  getSimpleBadge,
  getStateBadge,
} from 'src/app/shared/helpers/badgesBuilder';
import {
  getFirstLetterOfWord,
  setToTitleCase,
} from 'src/app/shared/helpers/transformText';
import { setTotalDays, setTotalHours } from 'src/app/shared/helpers/timeAndHours';
import { localDB } from 'src/app/shared/helpers/localStorage';
import { REQUEST_STATUS } from 'src/app/shared/components/marking/helpers/enums';
import { getIcon } from 'src/app/shared/helpers/iconBuilder';
import { GenericResponse } from 'src/app/shared/interfaces/generic.interface';
import { RequestList, footerList } from '../interfaces/interfaces';
import { configurations } from 'src/app/shared/interfaces/configurations.interface';
import { REQUEST_TYPE } from 'src/app/shared/interfaces/interface-manager-table';

function getRegulMarksGroupKey(idColaborador: number, dFechaInicio: string | Date | null): string {
  const jornadaDate = dFechaInicio ? new Date(dFechaInicio).getTime() : -Infinity;
  return `${idColaborador}_${jornadaDate}`;
}

function getCategoryName(categoryId: number): string {
  switch (categoryId) {
    case REQUEST_CATEGORY.PERMISOS: return 'Permisos';
    case REQUEST_CATEGORY.VACACIONES: return 'Vacaciones';
    case REQUEST_CATEGORY.LICENCIAS: return 'Licencias';
    case REQUEST_CATEGORY.HORAS_ADICIONALES: return 'Horas adicionales';
    case REQUEST_CATEGORY.DESCUENTOS: return 'Descuentos';
    case REQUEST_CATEGORY.REGULARIZACION_MARCAS: return 'Regularización de marcas';
    default: return '-';
  }
}

export function transforDataRequestList(
  response: GenericResponseList<RequestList>,
  _accessService: AccessService,
  configurations: { [key: string]: configurations[] }
) {
  if (response.data == null) response.data = [];
  let approvedDaysRequests: number = 0;
  let approvedHoursRequests: number = 0;
  let pendingDaysRequests: number = 0;
  let pendingHoursRequests: number = 0;

  const latestRegulMarksIdByGroup = new Map<string, number>();
  const nonAnnullableStates = new Set([REQUEST_STATUS.RECHAZADO, REQUEST_STATUS.ANULADO, REQUEST_STATUS.CADUCADO]);
  response.data.forEach(e => {
    if (e.nId_Categoria === REQUEST_CATEGORY.REGULARIZACION_MARCAS && !nonAnnullableStates.has(e.nEstado)) {
      const groupKey = getRegulMarksGroupKey(e.nId_Colaborador, e.dFecha_Inicio);
      const currentId = latestRegulMarksIdByGroup.get(groupKey) ?? -Infinity;
      if (e.nId > currentId) latestRegulMarksIdByGroup.set(groupKey, e.nId);
    }
  });

  response.data.forEach(function (e) {
    processRequestHistory(e, configurations);
    setupRequestDisplay(e, _accessService);
    setupRequestBadges(e);
    setupRequestActions(e, _accessService, latestRegulMarksIdByGroup);

    const counters = updateRequestCounters(e, approvedHoursRequests, approvedDaysRequests, pendingHoursRequests, pendingDaysRequests);
    approvedHoursRequests = counters.approvedHoursRequests;
    approvedDaysRequests = counters.approvedDaysRequests;
    pendingHoursRequests = counters.pendingHoursRequests;
    pendingDaysRequests = counters.pendingDaysRequests;
  });
  response.footer = {
    approvedHoursRequests: setTotalHours(approvedHoursRequests),
    pendingHoursRequests: setTotalHours(pendingHoursRequests),
    approvedDaysRequests: setTotalDays(approvedDaysRequests),
    pendingDaysRequests: setTotalDays(pendingDaysRequests),
  };


  return response;
}

export function getBadgeForRequestType(
  sRequestType: string,
  nIdCategory: number
) {
  let color = '';
  switch (nIdCategory) {
    case REQUEST_CATEGORY.PERMISOS:
      color = 'teal';
      break;

    case REQUEST_CATEGORY.LICENCIAS:
      color = 'main';
      break;

    case REQUEST_CATEGORY.VACACIONES:
      color = 'purple';
      break;

    case REQUEST_CATEGORY.HORAS_ADICIONALES:
      color = 'green';
      break;
    case REQUEST_CATEGORY.REGULARIZACION_MARCAS:
      color = 'main';
      break;
  }
  return getSimpleBadge(sRequestType, sRequestType, color, 'border !max-w-[13rem]');
}

export function getCategoryDotColor(nIdCategory: number): string[] {
  switch (nIdCategory) {
    case REQUEST_CATEGORY.PERMISOS:
      return ['bg-mg-teal', 'border', 'border-mg-teal-dark'];
    case REQUEST_CATEGORY.LICENCIAS:
      return ['bg-mg-main-blue', 'border', 'border-mg-main-blue-dark'];
    case REQUEST_CATEGORY.VACACIONES:
      return ['bg-mg-purple', 'border', 'border-mg-purple-dark'];
    case REQUEST_CATEGORY.HORAS_ADICIONALES:
      return ['bg-mg-green', 'border', 'border-mg-green-dark'];
    case REQUEST_CATEGORY.REGULARIZACION_MARCAS:
      return ['bg-mg-main-blue', 'border', 'border-mg-main-blue-dark'];
    default:
      return ['bg-mg-main-blue', 'border', 'border-mg-main-blue-dark'];
  }
}

export function setCantHoursOrDays(
  amount: number,
  idFrecuencyType: number,
  idRequestType: number
): string {
  if ([REQUEST_FREQUENCY_TYPE.POR_DIAS].includes(idFrecuencyType))
    return amount == 1 ? amount + ' día' : amount + ' días';

  if ([REQUEST_FREQUENCY_TYPE.POR_MESES].includes(idFrecuencyType))
    return amount == 1 ? amount + ' mes' : amount + ' meses';

  if (
    [REQUEST_FREQUENCY_TYPE.POR_HORAS].includes(idFrecuencyType) &&
    ![REQUEST_TYPE.EDICION_MARCAS, REQUEST_TYPE.REGISTRO_MARCAS, REQUEST_TYPE.ELIMINACION_MARCAS].includes(idRequestType)
  )
    return setTotalHours(amount);

  return '-';
}

export function approverRemoveDuplicate(aApprovers: string | null) {
  let nombres = [];
  let extras: string[] = [];
  let approvers: { id: string; name: string }[] = [];
  let id_aprobador: string = '';
  let parse_aapprovers: { id: string; name: string }[] = [];

  if (aApprovers == 'END') {
    nombres.push(getRoundBadge('Finalizado', '-'));
  } else {
    if (aApprovers != null) {
      parse_aapprovers = JSON.parse(aApprovers);
      parse_aapprovers.map((aprobador) => {
        if (aprobador.id != id_aprobador) {
          let abr = getFirstLetterOfWord(aprobador.name);
          if (nombres.length < 2) {
            nombres.push(getRoundBadge(setToTitleCase(aprobador.name), abr));
          } else {
            extras.push(setToTitleCase(aprobador.name))
          }
          approvers.push(aprobador);
        }
        id_aprobador = aprobador.id;
      });
      if (extras.length) {
        nombres.push(getRoundBadge(extras.join(', '), `+${extras.length}`));
      }
    }

    if (!nombres.length) {
      nombres.push(getRoundBadge('-', '-'));
    }
  }

  return {
    badge: nombres,
    approvers,
  };
}

export function showCancelIconAll(
  nIdCategoryType: number,
  nIdRequestType: number,
  nIdStatus: number,
  permission: boolean,
  idCollaborator: number
) {
  let idCollaboratorLocal = parseInt(localDB.getItem('idColaborador'));

  if (
    permission &&
    idCollaborator != idCollaboratorLocal &&
    ([
      REQUEST_CATEGORY.VACACIONES,
      REQUEST_CATEGORY.PERMISOS,
      REQUEST_CATEGORY.LICENCIAS,
    ].includes(nIdCategoryType) ||
      [REQUEST_TYPE.EDICION_MARCAS, REQUEST_TYPE.REGISTRO_MARCAS, REQUEST_TYPE.ELIMINACION_MARCAS].includes(nIdRequestType)) &&
    ![
      REQUEST_STATUS.ANULADO,
      REQUEST_STATUS.CADUCADO,
      REQUEST_STATUS.RECHAZADO,
    ].includes(nIdStatus)
  ) {
    if (nIdRequestType != 26) {
      return getIcon(
        { name: 'remove_circle_outline', type: 'material' },
        'Anular solicitud',
        'gray',
        true,
        'cancel-request'
      );
    }

  }

  if (
    idCollaborator == idCollaboratorLocal &&
    ([REQUEST_CATEGORY.VACACIONES, REQUEST_CATEGORY.PERMISOS].includes(
      nIdCategoryType
    ) ||
      [REQUEST_TYPE.EDICION_MARCAS, REQUEST_TYPE.REGISTRO_MARCAS, REQUEST_TYPE.ELIMINACION_MARCAS].includes(nIdRequestType)) &&
    [REQUEST_STATUS.PENDIENTE].includes(nIdStatus)
  ) {
    return getIcon(
      { name: 'remove_circle_outline', type: 'material' },
      'Anular solicitud',
      'gray',
      true,
      'cancel-request'
    );
  }

  return false;
}

export function sumHoursForApproveOrPending(idRequestType: number): boolean {
  if (
    [
      REQUEST_TYPE.PERMISO_POR_HORAS,
      REQUEST_TYPE.HORAS_COMPENSACION,
      REQUEST_TYPE.HORAS_EXTRAS,
      REQUEST_TYPE.HORAS_RECUPERACION,
      REQUEST_TYPE.SIN_GOCE_DE_HABER_POR_HORAS,
      REQUEST_TYPE.CON_GOCE_DE_HABER_POR_HORAS,
    ].includes(idRequestType)
  ) return true;

  return false;
}

export function sumDaysForApproveOrPending(idRequestType: number): boolean {
  if (
    [
      REQUEST_TYPE.PERMISO_POR_DIAS,
      REQUEST_TYPE.RESERVA_VACACIONES,
      REQUEST_TYPE.ADELANTO_DE_VACACIONES,
      REQUEST_TYPE.SIN_GOCE_DE_HABER_POR_DIAS,
      REQUEST_TYPE.CON_GOCE_DE_HABER_POR_DIAS,
    ].includes(idRequestType)
  )
    return true;

  return false;
}

function processRequestHistory(e: RequestList, configs: { [key: string]: configurations[] }): void {
  if (e.aHistory && e.nEstado == REQUEST_STATUS.ANULADO && e.aHistory.some(x => x.nId_Solicitud == e.nId) && e.nId_Requerimiento) {
    const oJson_Old = JSON.parse(e.aHistory.find(x => x.nId_Solicitud == e.nId).sJson_Old);
    if (oJson_Old && oJson_Old.nId_Proyecto) {
      enrichRequestFromHistory(e, oJson_Old, configs);
    }
  }
}

function enrichRequestFromHistory(e: RequestList, oJson_Old: any, configs: { [key: string]: configurations[] }): void {
  const proyectos = configs['PROYECTOS'];
  const requerimientos = configs['REQUERIMIENTOS_FILTRO'];
  const lideres_directores = configs['LIDERES_DIRECTORES'];
  const tipo_Hora = configs['TIPO_HORA'];
  const categorias = configs['CATEGORIAS_ALL'];
  const tipo_solicitud = configs['TIPO_SOLICITUD'];

  const tipo_tarea = tipo_Hora.find(x => x.sCodigo == oJson_Old.sTipo_Hora)!.sDescripcion;
  const tipo_sol = tipo_solicitud.find(x => x.sDescripcion.includes(tipo_tarea));

  e.sMotivo = oJson_Old.sDetalle;
  e.nId_Lider = oJson_Old.nId_Lider_Pry;
  e.sNombre_Lider = lideres_directores.find(x => x.sCodigo == oJson_Old.nId_Lider_Pry)!.sDescripcion;
  e.nId_Director = oJson_Old.nId_Director_Pry;
  e.sNombre_Director = lideres_directores.find(x => x.sCodigo == oJson_Old.nId_Director_Pry)!.sDescripcion;
  e.nId_Proyecto = oJson_Old.nId_Proyecto;
  e.sNombre_Proyecto = proyectos.find(x => x.sCodigo == oJson_Old.nId_Proyecto)!.sDescripcion;
  e.nId_Requerimiento = oJson_Old.nId_Requerimiento;
  e.sNombre_Requerimiento = requerimientos.find(x => x.sCodigo == oJson_Old.nId_Requerimiento)!.sDescripcion;
  e.nId_Categoria_requerimiento = Number(oJson_Old.sId_Categoria);
  e.sNombre_Categoria_requerimiento = categorias.find(x => x.sCodigo == oJson_Old.sId_Categoria)!.sDescripcion;
  e.nId_Tipo_Solicitud = Number(tipo_sol!.sCodigo);
  e.sNombre_Tipo_Solicitud = tipo_sol!.sDescripcion.split('-')[0];
}

function setupRequestDisplay(e: RequestList, accessService: AccessService): void {
  const name_pic: NameWithPic = {
    urlPic: e.sUrl_Foto,
    primaryText: { text: e.sNombre_Colaborador },
    secundaryText: { text: e.sDocumento_Completo },
    cssSubproperty: "font-normal uppercase text-xs"
  };

  e.icSeeDetails = getIcon(
    { name: 'visibility', type: 'material' },
    'Detalle',
    'main',
    accessService.validarPermisoSlug('REQUEST-DETAIL'),
    'view-request-details'
  );

  e.NameWithPic = getTextWithPic(name_pic);
  e.sNombre_Usuario_Update = (e.sNombre_Usuario_Update == '' || e.sNombre_Usuario_Update == null) ? '-' : e.sNombre_Usuario_Update;
}

function setupRequestBadges(e: RequestList): void {
  e.sNombre_Categoria = getCategoryName(e.nId_Categoria);
  e.bgRequestType = getBadgeForRequestType(e.sNombre_Tipo_Solicitud, e.nId_Categoria);
  e.bgState = getStateBadge(e.sEstado_Solicitud, e.sEstado_Solicitud);
  e.bgRange = getDateBagde(e.dFecha_Inicio, e.dFecha_Fin, e.nTipo_Frecuencia == REQUEST_FREQUENCY_TYPE.POR_HORAS);
  e.badgeUpdate = getRoundBadge(setToTitleCase(e.sNombre_Usuario_Update), getFirstLetterOfWord(e.sNombre_Usuario_Update), 'm-auto');
  e.bgAmount = getSimpleBadge(setCantHoursOrDays(e.nCantidad, e.nTipo_Frecuencia, e.nId_Tipo_Solicitud));

  const approvers = approverRemoveDuplicate(e.aprobadores);
  e.approvers = approvers.approvers;
  e.bgApprovers = approvers.badge;
}

function setupRequestActions(e: RequestList, accessService: AccessService, latestRegulMarksMap: Map<string, number>): void {
  const isRegulMarks = e.nId_Categoria === REQUEST_CATEGORY.REGULARIZACION_MARCAS;
  const isLatestRegulMarks = isRegulMarks && latestRegulMarksMap.get(getRegulMarksGroupKey(e.nId_Colaborador, e.dFecha_Inicio)) === e.nId;

  e.icCancelRequest = isRegulMarks && !isLatestRegulMarks
    ? false
    : showCancelIconAll(e.nId_Categoria, e.nId_Tipo_Solicitud, e.nEstado, accessService.validarPermisoSlug('REQUEST-CANCEL'), e.nId_Colaborador);

  e.btnHistory = e.nFlag_Historico
    ? {
        ...getIcon({ name: 'history', type: 'material' }, 'Historial de cambios', '', true, 'request_history'),
        action: 'request_history'
      }
    : false;

  e.actionMenuCards = [
    {
      label: 'Detalle',
      icon: { name: 'visibility', type: 'material', class: 'text-mg-main-blue' },
      type: 'submit',
      action: 'view-request-details',
      visible: accessService.validarPermisoSlug('REQUEST-DETAIL'),
    },
    {
      label: 'Anular solicitud',
      icon: { name: 'remove_circle_outline', type: 'material', class: 'text-mg-gray' },
      type: 'submit',
      action: 'cancel-request',
      visible: e.icCancelRequest ? true : false,
    },
  ];
}

function updateRequestCounters(
  e: RequestList,
  approvedHours: number,
  approvedDays: number,
  pendingHours: number,
  pendingDays: number
): { approvedHoursRequests: number; approvedDaysRequests: number; pendingHoursRequests: number; pendingDaysRequests: number } {
  const isApproved = e.sEstado_Solicitud == 'APROBADO';
  const isPending = ['PENDIENTE', 'PENDIENTE POR DIRECTOR'].includes(e.sEstado_Solicitud);

  if (isApproved && sumHoursForApproveOrPending(e.nId_Tipo_Solicitud)) {
    approvedHours += e.nCantidad;
  }

  if (isPending && sumHoursForApproveOrPending(e.nId_Tipo_Solicitud)) {
    pendingHours += e.nCantidad;
  }

  if (isApproved && sumDaysForApproveOrPending(e.nId_Tipo_Solicitud)) {
    approvedDays += e.nCantidad;
  }

  if (isPending && sumDaysForApproveOrPending(e.nId_Tipo_Solicitud)) {
    pendingDays += e.nCantidad;
  }

  return {
    approvedHoursRequests: approvedHours,
    approvedDaysRequests: approvedDays,
    pendingHoursRequests: pendingHours,
    pendingDaysRequests: pendingDays,
  };
}

export function footerTransform(response: GenericResponse<footerList>,) {
  if (response.isSuccess && response.data != null) {
    response.data.approvedHoursRequests = setTotalHours(response.data.solicitudesHorasAprobadas);
    response.data.pendingHoursRequests = setTotalHours(response.data.solicitudesHorasPendientes);
    response.data.approvedDaysRequests = setTotalDays(response.data.solicitudesDiasAprobadas);
    response.data.pendingDaysRequests = setTotalDays(response.data.solicitudesDiasPendientes);
  }
  return response;
}